import { useEffect, useMemo, useState } from 'react';
import {
  createTranslator,
  localeCatalogues,
  resolveUiLocale,
  type Catalogues,
  type UiLocale,
} from '@skm/i18n';
import { UiI18nContext } from '@skm/ui';
import { screenModules } from '../_generated/routes.ts';
import type { ApiClient } from '../api/client.ts';
import {
  ACCEPT_INVITE_PATH,
  AcceptInvite,
  createAuth,
  SignIn,
  type Principal,
} from '../auth/index.ts';
import { landingRoute, Shell } from '../shell/index.ts';
import { ScreenHost } from './ScreenHost.tsx';
import type { ScreenModuleEntry } from './screens.ts';

export interface AppProps {
  readonly api: ApiClient;
  readonly catalogues?: Catalogues;
  readonly initialPath?: string;
  /** Route modules (default: the ones `pnpm gen` found under src/routes); tests inject their own. */
  readonly modules?: readonly ScreenModuleEntry[];
}

/**
 * Root: locale + translator, session, landing and route → screen (route modules mount via ScreenHost).
 * `/accept-invite` is served outside the shell to anyone (the link's fragment token is the credential); finishing
 * it ends any session already open in this browser and goes to sign-in [CR-W2a-42 Q4].
 */
export function App({
  api,
  catalogues = localeCatalogues as Catalogues,
  initialPath,
  modules = screenModules,
}: AppProps) {
  const auth = useMemo(() => createAuth(api), [api]);
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [locale, setLocale] = useState<UiLocale>(() =>
    resolveUiLocale(null, typeof navigator === 'undefined' ? null : navigator.language),
  );
  const [path, setPath] = useState(
    initialPath ?? (typeof location === 'undefined' ? '/' : location.pathname),
  );
  const t = useMemo(() => createTranslator({ catalogues, locale }).t, [catalogues, locale]);

  useEffect(() => {
    void auth.me().then(
      (p) => p && signedIn(p),
      () => undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  // Browser Back / Forward: the URL changed without going through navigate().
  useEffect(() => {
    const onPop = () => setPath(location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function signedIn(p: Principal) {
    setPrincipal(p);
    setLocale(resolveUiLocale(p.locale, null));
    if (path === '/' || path === '') navigate(landingRoute(p.permissions, p.landing, null));
  }
  function navigate(route: string) {
    setPath(route);
    if (typeof history !== 'undefined') history.pushState(null, '', route);
  }

  if (path === ACCEPT_INVITE_PATH)
    return (
      <UiI18nContext.Provider value={t}>
        <AcceptInvite
          auth={auth}
          onDone={() => {
            if (principal) void auth.signOut().catch(() => undefined);
            setPrincipal(null);
            navigate('/');
          }}
        />
      </UiI18nContext.Provider>
    );

  return (
    <UiI18nContext.Provider value={t}>
      {principal ? (
        <Shell
          principal={principal}
          {...(principal.featureFlags ? { featureFlags: principal.featureFlags } : {})}
          locale={locale}
          onLocaleChange={setLocale}
          onNavigate={navigate}
          onSignOut={() => {
            void auth.signOut().finally(() => setPrincipal(null));
          }}
        >
          <ScreenHost
            path={path}
            api={api}
            permissions={principal.permissions}
            navigate={navigate}
            modules={modules}
          />
        </Shell>
      ) : (
        <SignIn auth={auth} onSignedIn={signedIn} />
      )}
    </UiI18nContext.Provider>
  );
}
